import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserCog, ChevronLeft, Search, Loader2, ArrowUpDown, Users,
  Plus, Pencil, Trash2, X, Save, Mail, Phone as PhoneIcon, ShieldCheck,
} from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import ToastMsg from '../../../components/common/ToastMsg';
import { cn } from '../../../utils/tailwindUtils';
import { ROLES_URL } from '../../../constants/api';

// Password rule mirrors the backend PASSWORD_REGEX.
const STRONG_PW = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W)[a-zA-Z0-9\S]{12,}$/;


const ACTIVE_STATUSES = new Set([
  'new', 'contacted', 'interested', 'appointment_booked', 'consulted',
  'under_treatment', 'follow_up_required',
]);

const ROLE_OPTIONS = [
  { value: 'user',        label: 'User' },
  { value: 'admin',       label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'manager',     label: 'Manager' },
];


export default function UsersSettings() {
  const navigate = useNavigate();
  const { fetch: apiFetch, create: apiCreate, update: apiUpdate } = useApiMethods({ throwOnFail: true });

  const [users, setUsers] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('open');
  const [sortDir, setSortDir] = useState('desc');
  const [reloadTick, setReloadTick] = useState(0);

  const [editingUser, setEditingUser] = useState(null);   // null = closed, {} = add, {...} = edit
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uResp, eResp] = await Promise.all([
        apiFetch('auth/login/create/?page_size=200'),
        apiFetch('engage/enquiries/?limit=500'),
      ]);
      const uRows = uResp?.data || uResp?.data?.data || [];
      const eRows = eResp?.results || eResp?.data?.results || [];
      setUsers(Array.isArray(uRows) ? uRows : []);
      setEnquiries(Array.isArray(eRows) ? eRows : []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  useEffect(() => { load(); }, [load]);

  // Per-user stats from enquiries
  const statsByUserId = useMemo(() => {
    const m = {};
    const now = Date.now();
    const thirtyDaysAgo = now - 30 * 86400000;
    for (const e of enquiries) {
      const uid = e.assigned_to;
      if (!uid) continue;
      if (!m[uid]) m[uid] = { open: 0, converted_30d: 0, handled_30d: 0, hot: 0, overdue: 0 };
      const s = m[uid];
      if (ACTIVE_STATUSES.has(e.status)) s.open += 1;
      if (['interested', 'appointment_booked', 'consulted'].includes(e.status)) s.hot += 1;
      if (ACTIVE_STATUSES.has(e.status) && e.followup_datetime
          && new Date(e.followup_datetime).getTime() < now) s.overdue += 1;
      if (e.created_date && new Date(e.created_date).getTime() >= thirtyDaysAgo) s.handled_30d += 1;
      if (e.status === 'converted' && e.modified_date && new Date(e.modified_date).getTime() >= thirtyDaysAgo) {
        s.converted_30d += 1;
      }
    }
    Object.values(m).forEach(s => {
      s.conv_rate = s.handled_30d ? Math.round(s.converted_30d / s.handled_30d * 100) : 0;
    });
    return m;
  }, [enquiries]);

  const rows = useMemo(() => {
    if (!users) return [];
    return users.map(u => {
      const name = `${u.login_fname || ''} ${u.login_lname || ''}`.trim() || u.login_name || `#${u.id}`;
      const s = statsByUserId[u.id] || { open: 0, converted_30d: 0, handled_30d: 0, hot: 0, overdue: 0, conv_rate: 0 };
      return { ...u, name, ...s };
    });
  }, [users, statsByUserId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let r = rows;
    if (q) {
      r = r.filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.login_name || '').toLowerCase().includes(q) ||
        (u.login_email_address || '').toLowerCase().includes(q)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...r].sort((a, b) => {
      const x = a[sortKey], y = b[sortKey];
      if (typeof x === 'number') return (x - y) * dir;
      return (x || '').toString().toLowerCase() < (y || '').toString().toLowerCase() ? -1 * dir : 1 * dir;
    });
  }, [rows, search, sortKey, sortDir]);

  const totals = useMemo(() => ({
    users: rows.length,
    assigned: rows.filter(r => r.open > 0).length,
    total_open: rows.reduce((s, r) => s + r.open, 0),
    total_converted: rows.reduce((s, r) => s + r.converted_30d, 0),
  }), [rows]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'name' ? 'asc' : 'desc'); }
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      await apiCreate('auth/login/create/', payload);
      ToastMsg('success', payload.id ? 'User updated' : 'User created');
      setEditingUser(null);
      setReloadTick(t => t + 1);
    } catch (e) {
      ToastMsg('error', e?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Deactivate user "${u.name}"? They won't be able to log in until re-activated.`)) return;
    try {
      await apiUpdate('auth/login/create/', { id: u.id, draft_delete: true });
      ToastMsg('success', 'User deactivated');
      setReloadTick(t => t + 1);
    } catch (e) {
      ToastMsg('error', 'Failed to deactivate user');
    }
  };

  return (
    <div className="p-4 max-w-[1200px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/dashboard')}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-sm">
            <UserCog className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Users</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Team members with Engage access. Click a row to see their assigned enquiries.
            </p>
          </div>
        </div>
        <button onClick={() => setEditingUser({})}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700">
          <Plus className="w-3.5 h-3.5" /> Add user
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total users"          value={totals.users}          icon={Users}   tone="bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400" />
        <StatCard label="With active load"     value={totals.assigned}       icon={UserCog} tone="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" />
        <StatCard label="Total open pipeline"  value={totals.total_open}     icon={UserCog} tone="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400" />
        <StatCard label="Converted (30d)"      value={totals.total_converted} icon={UserCog} tone="bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400" valueTone="text-green-600 dark:text-green-400" />
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, username, or email…"
            className="w-full pl-10 pr-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <ThSort label="User"           onClick={() => toggleSort('name')}          active={sortKey === 'name'}          dir={sortDir} />
              <th className="px-4 py-2 text-left font-medium">Contact</th>
              <th className="px-4 py-2 text-left font-medium">Role</th>
              <ThSort label="Open"           onClick={() => toggleSort('open')}          active={sortKey === 'open'}          dir={sortDir} right />
              <ThSort label="Overdue"        onClick={() => toggleSort('overdue')}       active={sortKey === 'overdue'}       dir={sortDir} right />
              <ThSort label="Conv (30d)"     onClick={() => toggleSort('converted_30d')} active={sortKey === 'converted_30d'} dir={sortDir} right />
              <ThSort label="Conv %"         onClick={() => toggleSort('conv_rate')}     active={sortKey === 'conv_rate'}     dir={sortDir} right />
              <th className="px-4 py-2 text-center font-medium w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading || users === null ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" /> Loading…
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                {search ? `No users match "${search}"` : 'No users yet. Click "Add user" to create one.'}
              </td></tr>
            ) : filtered.map(u => (
              <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/engage/enquiries?assigned_to=${u.id}`)}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0">
                      {(u.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{u.name}</div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">@{u.login_name || '—'}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-[12px] text-gray-600 dark:text-gray-400">
                  {u.login_email_address && (
                    <div className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{u.login_email_address}</div>
                  )}
                  {u.login_phone_number && (
                    <div className="inline-flex items-center gap-1 mt-0.5"><PhoneIcon className="w-3 h-3" />{u.login_phone_number}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.role_name || u.role_type ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                      <ShieldCheck className="w-3 h-3" /> {u.role_name || u.role_type}
                    </span>
                  ) : <span className="text-gray-400 text-[11px]">—</span>}
                  {!u.is_active && (
                    <div className="mt-0.5">
                      <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                        Inactive
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white tabular-nums">{u.open}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {u.overdue > 0 ? <span className="text-red-500 font-medium">{u.overdue}</span> : <span className="text-gray-400">0</span>}
                </td>
                <td className="px-4 py-3 text-right text-green-600 dark:text-green-400 font-medium tabular-nums">{u.converted_30d}</td>
                <td className="px-4 py-3 text-right">
                  <span className={cn(
                    'inline-block px-2 py-0.5 rounded-full text-[10px] font-medium tabular-nums',
                    u.conv_rate >= 30 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                    u.conv_rate >= 15 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                                        'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300',
                  )}>
                    {u.conv_rate}%
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={(e) => { e.stopPropagation(); setEditingUser(u); }}
                      className="p-1.5 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      title="Edit user">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {u.is_active && (
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(u); }}
                        className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Deactivate user">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/edit modal */}
      {editingUser && (
        <UserFormModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Add/Edit User modal
// ────────────────────────────────────────────────────────────────────────────

function UserFormModal({ user, onClose, onSave, saving }) {
  const isEdit = !!user.id;
  const { fetch: apiFetch } = useApiMethods({ throwOnFail: true });
  const [form, setForm] = useState({
    id:                  user.id || undefined,
    login_fname:         user.login_fname || '',
    login_lname:         user.login_lname || '',
    user_name:           user.login_name || '',
    login_email_address: user.login_email_address || '',
    login_phone_number:  user.login_phone_number || '',
    emp_code:            user.emp_code || '',
    role_type:           user.role_type || 'user',
    assigned_role_id:    user.assigned_role_id || null,
    is_active:           user.is_active !== false,
    password:            '',
  });
  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);

  // Load the custom roles for the assigned-role dropdown.
  useEffect(() => {
    let alive = true;
    apiFetch(ROLES_URL).then(res => {
      if (alive && res?.status === 'success') setRoles(res.data || []);
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    // A custom role only applies to a 'user'; clear it for admin/super_admin.
    if (k === 'role_type' && v !== 'user') next.assigned_role_id = null;
    return next;
  });

  const validate = () => {
    const e = {};
    if (!form.login_fname.trim())         e.login_fname = 'First name is required';
    if (!form.user_name.trim())           e.user_name = 'Username is required';
    if (!form.login_email_address.trim()) e.login_email_address = 'Email is required';
    if (!form.login_phone_number.trim())  e.login_phone_number = 'Phone is required';
    if (form.role_type === 'user' && !form.assigned_role_id) {
      e.assigned_role_id = 'Select a role for this user';
    }
    if (!isEdit && !form.password.trim()) e.password = 'Password is required for a new user';
    if (form.password && !STRONG_PW.test(form.password)) {
      e.password = 'At least 12 characters, incl. upper, lower, number, special';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    const payload = { ...form, assigned_role_id: form.assigned_role_id || null };
    if (!payload.password) delete payload.password;
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-xl">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
            <UserCog className="w-4 h-4 text-indigo-500" /> {isEdit ? 'Edit user' : 'Add user'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="First name" required value={form.login_fname} onChange={v => set('login_fname', v)} error={errors.login_fname} />
            <FormField label="Last name" value={form.login_lname} onChange={v => set('login_lname', v)} />
          </div>
          <FormField label="Username / login" required value={form.user_name} onChange={v => set('user_name', v)} error={errors.user_name}
            hint="Used to log in — cannot contain spaces." />
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Email" required type="email" value={form.login_email_address} onChange={v => set('login_email_address', v)} error={errors.login_email_address} />
            <FormField label="Phone" required value={form.login_phone_number} onChange={v => set('login_phone_number', v)} error={errors.login_phone_number} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Employee code" value={form.emp_code} onChange={v => set('emp_code', v)} />
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-1">User Type</label>
              <select value={form.role_type} onChange={e => set('role_type', e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white">
                {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>

          {/* Custom role — determines page access for a 'user'. Admins get everything. */}
          {form.role_type === 'user' ? (
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-1">
                Role <span className="text-red-500">*</span>
              </label>
              <select value={form.assigned_role_id || ''} onChange={e => set('assigned_role_id', e.target.value ? Number(e.target.value) : null)}
                className={cn(
                  "w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border text-gray-900 dark:text-white",
                  errors.assigned_role_id ? "border-red-500" : "border-gray-200 dark:border-gray-700"
                )}>
                <option value="">Select a role…</option>
                {roles.filter(r => r.is_active).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <p className="text-[11px] text-gray-400 mt-1">The role's ticked pages become this user's access.</p>
              {errors.assigned_role_id && <p className="text-[11px] text-red-500 mt-0.5">{errors.assigned_role_id}</p>}
            </div>
          ) : (
            <p className="text-[11px] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
              {form.role_type === 'super_admin' ? 'Super admins' : 'Admins'} have full access — no role needed.
            </p>
          )}
          <FormField label={isEdit ? 'Reset password (optional)' : 'Password'}
            required={!isEdit} type="password" value={form.password} onChange={v => set('password', v)} error={errors.password}
            hint="Min 12 chars — must include upper, lower, number, and a special character." />
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mt-1">
            <input type="checkbox" checked={!!form.is_active} onChange={e => set('is_active', e.target.checked)} />
            Active (can log in)
          </label>
        </div>

        <div className="p-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
          <button onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : (isEdit ? 'Update user' : 'Create user')}
          </button>
        </div>
      </div>
    </div>
  );
}


function FormField({ label, value, onChange, type = 'text', required, error, hint }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
        className={cn(
          'w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border text-gray-900 dark:text-white focus:outline-none focus:ring-2',
          error ? 'border-red-400 focus:ring-red-500' : 'border-gray-200 dark:border-gray-700 focus:ring-indigo-500',
        )} />
      {error && <p className="text-[10px] text-red-500 mt-0.5">{error}</p>}
      {!error && hint && <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}


function StatCard({ label, value, icon: Icon, tone, valueTone }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">{label}</p>
          <p className={cn('text-2xl font-bold mt-1', valueTone || 'text-gray-900 dark:text-white')}>{value}</p>
        </div>
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', tone)}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}


function ThSort({ label, onClick, active, dir, right }) {
  return (
    <th className={cn('px-4 py-2 font-medium', right ? 'text-right' : 'text-left')}>
      <button onClick={onClick} className={cn(
        'inline-flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200 transition-colors',
        active && 'text-gray-900 dark:text-white',
      )}>
        {label}
        <ArrowUpDown className={cn('w-3 h-3', active ? 'text-indigo-500' : 'text-gray-300')} />
        {active && <span className="text-[8px]">{dir === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}
